import { useRef } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { TrendingUp, RefreshCw, Package, Handshake, Milestone, Share2, Scale } from "lucide-react";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";

const EASE = [0.16, 1, 0.3, 1];

const STEPS = [
  { label: "Seen industries evolve", Icon: TrendingUp },
  { label: "Managed through cycles", Icon: RefreshCw },
  { label: "Built products", Icon: Package },
  { label: "Sold to customers", Icon: Handshake },
  { label: "Made mistakes", Icon: Milestone },
  { label: "Built networks", Icon: Share2 },
  { label: "Developed judgment", Icon: Scale },
];

function Step({ label, Icon, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-20%" });
  return (
    <motion.li
      ref={ref}
      data-testid={`timeline-step-${index + 1}`}
      initial={{ opacity: 0, x: 24 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.7, ease: EASE }}
      className="relative flex items-center gap-5 py-6 pl-16"
    >
      <span
        className={`absolute left-[15px] flex h-9 w-9 -translate-x-1/2 items-center justify-center rounded-full border transition-all duration-500 ${
          inView ? "border-gold bg-noir text-gold" : "border-white/15 bg-noir text-cream/25"
        }`}
      >
        <Icon size={16} strokeWidth={1.25} />
      </span>
      <span className="font-serif text-3xl tracking-tight text-cream md:text-4xl">
        {label}
      </span>
    </motion.li>
  );
}

export default function Preparation() {
  const listRef = useRef(null);
  const reduced = usePrefersReducedMotion();
  const { scrollYProgress } = useScroll({ target: listRef, offset: ["start 0.7", "end 0.6"] });
  const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section
      id="preparation"
      data-testid="preparation-section"
      className="relative scroll-mt-24 border-t border-white/10 bg-noir py-24 md:py-32 lg:py-40"
    >
      <div className="mx-auto w-full max-w-7xl px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-16 text-[0.72rem] font-semibold uppercase tracking-[0.32em] text-gold"
        >
          02 &mdash; The Preparation
        </motion.p>

        <div className="grid grid-cols-1 gap-16 md:grid-cols-12 md:gap-x-0">
          <div className="md:col-span-5 md:sticky md:top-32 md:self-start md:pr-12">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: EASE }}
              className="max-w-md font-serif leading-[1.02] tracking-tight text-cream"
              style={{ fontSize: "clamp(2.5rem, 4vw, 3.75rem)" }}
            >
              Some entrepreneurs spend decades preparing without realizing it.
            </motion.h2>
          </div>

          <div ref={listRef} className="relative md:col-span-7 md:pl-12">
            <span className="absolute left-[15px] top-0 h-full w-px -translate-x-1/2 bg-white/10" />
            <motion.span
              style={{ scaleY: reduced ? 1 : lineScale }}
              className="absolute left-[15px] top-0 h-full w-px origin-top -translate-x-1/2 bg-gold"
            />
            <ul className="relative">
              {STEPS.map((s, i) => (
                <Step key={s.label} label={s.label} Icon={s.Icon} index={i} />
              ))}
            </ul>
          </div>
        </div>

        <div className="mx-auto mt-28 max-w-3xl md:mt-36">
          <motion.p
            data-testid="preparation-transition"
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-15%" }}
            transition={{ duration: 0.9, ease: EASE }}
            className="font-serif leading-[1.14] tracking-tight text-cream"
            style={{ fontSize: "clamp(1.9rem, 3vw, 2.9rem)" }}
          >
            Then, at some point, they see an opportunity clearly. The problem is
            that entrepreneurship still requires starting from zero.
          </motion.p>
          <motion.p
            data-testid="preparation-closer"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-15%" }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
            className="mt-10 font-quote tracking-tight text-gold"
            style={{ fontSize: "clamp(2rem, 3.6vw, 3.25rem)" }}
          >
            Govil Ventures changes that.
          </motion.p>
        </div>
      </div>
    </section>
  );
}
